"use client";

import { EyeIcon, ArrowRightIcon, CheckIcon } from "lucide-react";
import { Button } from "@ui/components/button";
import { LocaleLink } from "@i18n/routing";

interface AIVisibilityReportSectionProps {
  badge: string;
  title: string;
  description: string;
  price: string;
  features: string[];
  ctaText: string;
  className?: string;
}

export function AIVisibilityReportSection({ 
  badge, 
  title, 
  description, 
  price, 
  features, 
  ctaText,
  className 
}: AIVisibilityReportSectionProps) {
  return (
    <div className={`py-8 ${className || ''}`}>
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <div className="bg-background rounded-2xl border p-8 shadow-sm">
            <div className="flex flex-col lg:flex-row lg:items-center gap-8">
              {/* Left side - Description */}
              <div className="flex-1">
                <h2 className="text-2xl lg:text-3xl font-bold mb-3">
                  {title}
                </h2>
                <p className="text-muted-foreground mb-4">
                  {description}
                </p>
                
                <div className="grid sm:grid-cols-2 gap-2">
                  {features.map((feature, index) => (
                    <div key={index} className="flex items-start gap-2">
                      <CheckIcon className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                      <span className="text-sm">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Right side - Pricing & CTA */}
              <div className="lg:w-80 flex-shrink-0">
                <div className="text-center lg:text-left">
                  <div className="mb-4">
                    <div className="text-sm text-muted-foreground mb-1">Unlock full report</div>
                    <div className="text-3xl font-bold">{price}</div>
                    <div className="text-sm text-muted-foreground">one-time payment</div>
                  </div>
                  
                  <Button size="lg" className="w-full" asChild>
                    <LocaleLink href="/ai-visibility-report">
                      {ctaText}
                      <ArrowRightIcon className="w-4 h-4 ml-2" />
                    </LocaleLink>
                  </Button>
                  
                  <p className="text-xs text-muted-foreground mt-2">
                    Generate free report first, then unlock insights
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
